import { type Editor, useEditorState } from "@tiptap/react";
import clsx from "clsx";
import React from "react";
import Button from "../../common/Button/Button";
import DropdownMenu from "../../common/DropdownMenu/DropdownMenu";
import Icon, { type IconName } from "../../common/Icon/Icon";
import styles from "./RichTextEditor.module.scss";
import { preserveSelection, useRichTextEditor } from "./context";

export interface RichTextEditorMenuOption {
  id: string;
  label: string;
  icon: IconName;
  isActive: (editor: Editor) => boolean;
  /** Hides the option when false, e.g. a heading level the editor isn't configured for. */
  isAvailable?: (editor: Editor) => boolean;
  /** Draws a divider above this option. */
  separatorBefore?: boolean;
  onSelect: (editor: Editor) => void;
}

export interface RichTextEditorMenuProps {
  /** Accessible name and tooltip of the trigger. */
  label: string;
  /** Small heading at the top of the menu. */
  heading?: string;
  options: RichTextEditorMenuOption[];
  /**
   * What the trigger shows for the active option: its icon (`"icon"`, default)
   * or its label (`"text"`). The first option stands in when none is active.
   */
  trigger?: "icon" | "text";
  /** Shows the trigger as pressed while any option is active. */
  selectedWhenActive?: boolean;
}

/**
 * Toolbar dropdown of mutually exclusive block options (block type, list type,
 * alignment). The trigger reflects the option active at the cursor, and the
 * active option is ticked in the menu.
 */
export const RichTextEditorMenu = ({
  label,
  heading,
  options,
  trigger = "icon",
  selectedWhenActive = false,
}: RichTextEditorMenuProps) => {
  const { editor, disabled } = useRichTextEditor();
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      if (!current) return { activeId: null, availableIds: "" };
      const available = options.filter((option) => option.isAvailable?.(current) ?? true);
      return {
        activeId: available.find((option) => option.isActive(current))?.id ?? null,
        // A string so the selector result compares by value between transactions.
        availableIds: available.map((option) => option.id).join(","),
      };
    },
  });
  const availableIds = (state?.availableIds ?? "").split(",");
  const visible = options.filter((option) => availableIds.includes(option.id));
  const active = visible.find((option) => option.id === state?.activeId);
  const shown = active ?? visible[0] ?? options[0];

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button
          size="sm"
          variant="ghost"
          label={label}
          tooltip={label}
          selected={selectedWhenActive && Boolean(active)}
          disabled={disabled || !editor}
          className={clsx(styles.menuTrigger, "py-0 px-1")}
          onMouseDown={preserveSelection}
        >
          {trigger === "text" ? (
            <span className={styles.menuTriggerLabel}>{shown.label}</span>
          ) : (
            <Icon
              name={shown.icon}
              aria-hidden
            />
          )}
          <Icon
            name="expand"
            className={styles.menuChevron}
            aria-hidden
          />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        className={styles.menu}
        // Hand focus back to the editor, not the trigger, so typing carries on.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          editor?.commands.focus();
        }}
      >
        {heading ? <DropdownMenu.Label className={styles.menuHeading}>{heading}</DropdownMenu.Label> : null}
        {visible.map((option) => (
          <React.Fragment key={option.id}>
            {option.separatorBefore ? <DropdownMenu.Separator /> : null}
            <DropdownMenu.Item
              className={styles.menuItem}
              onSelect={() => editor && option.onSelect(editor)}
            >
              <Icon
                name={option.icon}
                aria-hidden
              />
              <DropdownMenu.Item.Label>{option.label}</DropdownMenu.Item.Label>
              {option === active ? (
                <Icon
                  name="check"
                  aria-label="Current"
                  className={styles.menuCheck}
                />
              ) : null}
            </DropdownMenu.Item>
          </React.Fragment>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

const headingLevels = (editor: Editor): number[] =>
  editor.extensionManager.extensions.find((extension) => extension.name === "heading")?.options.levels ?? [];

const hasExtension = (name: string) => (editor: Editor) =>
  editor.extensionManager.extensions.some((extension) => extension.name === name);

const headingOption = (level: 1 | 2 | 3 | 4): RichTextEditorMenuOption => ({
  id: `heading-${level}`,
  label: `Heading ${level}`,
  icon: `heading-${level}`,
  isActive: (editor) => editor.isActive("heading", { level }),
  isAvailable: (editor) => headingLevels(editor).includes(level),
  onSelect: (editor) => editor.chain().focus().setHeading({ level }).run(),
});

export const TURN_INTO_OPTIONS: RichTextEditorMenuOption[] = [
  {
    id: "paragraph",
    label: "Text",
    icon: "typography",
    // A quote holds paragraphs, so Text is only current outside one.
    isActive: (editor) => editor.isActive("paragraph") && !editor.isActive("blockquote"),
    // Lifts out of a list or quote as well as clearing a heading or code block.
    onSelect: (editor) => editor.chain().focus().clearNodes().setParagraph().run(),
  },
  headingOption(1),
  headingOption(2),
  headingOption(3),
  headingOption(4),
  {
    id: "codeBlock",
    label: "Code block",
    icon: "code-block",
    separatorBefore: true,
    isActive: (editor) => editor.isActive("codeBlock"),
    isAvailable: hasExtension("codeBlock"),
    onSelect: (editor) => editor.chain().focus().setCodeBlock().run(),
  },
  {
    id: "blockquote",
    label: "Quote",
    icon: "quote",
    isActive: (editor) => editor.isActive("blockquote"),
    isAvailable: hasExtension("blockquote"),
    onSelect: (editor) =>
      editor.isActive("blockquote")
        ? editor.commands.focus()
        : editor.chain().focus().clearNodes().setBlockquote().run(),
  },
];

export const LIST_OPTIONS: RichTextEditorMenuOption[] = [
  {
    id: "bulletList",
    label: "Bulleted list",
    icon: "list",
    isActive: (editor) => editor.isActive("bulletList"),
    onSelect: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: "orderedList",
    label: "Numbered list",
    icon: "list-ordered",
    isActive: (editor) => editor.isActive("orderedList"),
    onSelect: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
];

const alignOption = (alignment: "left" | "center" | "right" | "justify", label: string): RichTextEditorMenuOption => ({
  id: alignment,
  label,
  icon: `align-${alignment}`,
  isActive: (editor) => editor.isActive({ textAlign: alignment }),
  onSelect: (editor) => editor.chain().focus().setTextAlign(alignment).run(),
});

export const ALIGN_OPTIONS: RichTextEditorMenuOption[] = [
  alignOption("left", "Align left"),
  alignOption("center", "Align center"),
  alignOption("right", "Align right"),
  alignOption("justify", "Justify"),
];

/** Block type: Text, Heading 1-4 (as many as the heading extension allows), Code block, Quote. */
export const RichTextEditorTurnInto = () => (
  <RichTextEditorMenu
    label="Turn into"
    heading="Turn into"
    trigger="text"
    options={TURN_INTO_OPTIONS}
  />
);

/** Bulleted or numbered list. Picking the current list type again removes the list. */
export const RichTextEditorLists = () => (
  <RichTextEditorMenu
    label="List"
    options={LIST_OPTIONS}
    selectedWhenActive
  />
);

/** Alignment of the rows at the selection. Needs `RowTextAlign` (or `TextAlign`). */
export const RichTextEditorAlign = () => (
  <RichTextEditorMenu
    label="Alignment"
    options={ALIGN_OPTIONS}
  />
);
